import { useMemo } from "react";
import { useDashboardContext } from "../contexts/dashboard-context";
import { SpreadDesignId } from "../types/SpreadDesignId";
import { jsonComparer, useMessageToState } from "./useMessageToState";
import { useSelectedSpread } from "./useSelection";

/** A list of an ID that can be used for selecting and information to show and apply the spread design */
interface SpreadDesign {
	id: SpreadDesignId;
	pageDesigns: eXpress.creator.IConfigurationLayoutItem[];
}

function getThumbUrlAsync(
	layoutId: string,
	editor: Pick<eXpress.core.IEditor, "definition" | "client">,
): string {
	const product = editor?.definition?.product;
	if (!product) return "";
	const relativeUrl = new eXpress.api.PageThumbUrl(
		product.classification,
		product.designType,
		product.predefinedName,
		layoutId,
	);
	const absoluteUrl = relativeUrl.getAbsoluteUrl(editor.client);

	return absoluteUrl;
}

const getSpreadDesigns = (
	definition: eXpress.core.IDefinition | null,
	client: eXpress.core.IClient,
) => {
	if (
		definition?.product.configurationLayouts ===
		eXpress.core.ConfigurationLayoutsSetting.Disabled
	)
		return [];

	const spreadDesigns = eXpress.creator.getSpreadDesigns(definition).map((pageDesigns) => {
		const spreadDesign: SpreadDesign = {
			id: SpreadDesignId.fromPageDesigns(pageDesigns),
			pageDesigns: pageDesigns.map((pageDesign) => ({
				...pageDesign,
				url: getThumbUrlAsync(pageDesign.id, {
					definition,
					client,
				}),
			})),
		};
		return spreadDesign;
	});

	return spreadDesigns;
};

const getSpreadDesignsForEditor = (editor: eXpress.core.IEditor) =>
	getSpreadDesigns(editor.definition, editor.client);

function useAllSpreadDesigns() {
	return useMessageToState({
		name: "useAllSpreadDesigns",
		filterMessage: (message) =>
			eXpress.core.isNewDefinitionLoaded(message) ||
			eXpress.core.isSelectedConstellationChanged(message),
		getState: getSpreadDesignsForEditor,
		comparer: jsonComparer,
	});
}

const defaultSpreadDesigns: SpreadDesign[] = [];

export function useSpreadDesigns() {
	const allSpreadDesigns = useAllSpreadDesigns();
	const spread = useSelectedSpread();
	const dashboard = useDashboardContext();

	return useMemo(() => {
		if (!spread) return defaultSpreadDesigns;

		const canReplaceLeftPage = dashboard.canReplacePage(spread?.leftPage);
		const canReplaceRightPage = dashboard.canReplacePage(spread?.rightPage);

		const isCover = spread?.kind === eXpress.core.SpreadKind.Cover;

		const availableSpreadDesigns = allSpreadDesigns.filter((spreadDesign) => {
			const pagesPerSpread = spread.container?.pagesPerSpread ?? 1;
			if (pagesPerSpread > 1) {
				// multipage
				if (spread?.pageCount === spreadDesign.pageDesigns.length) {
					return spreadDesign.pageDesigns.every((pageDesign) => {
						if (
							isCover &&
							pageDesign.placement.hasAll(eXpress.creator.LayoutPlacement.Single) &&
							canReplaceLeftPage
						) {
							return true;
						}
						if (
							!isCover &&
							pageDesign.placement.hasAll(eXpress.creator.LayoutPlacement.Left) &&
							canReplaceLeftPage
						) {
							return true;
						}
						if (
							!isCover &&
							pageDesign.placement.hasAll(eXpress.creator.LayoutPlacement.Right) &&
							canReplaceRightPage
						) {
							return true;
						}
						return false;
					});
				}
			} else {
				// single page
				return spreadDesign.pageDesigns.every((pageDesign) => {
					if (
						pageDesign.placement.hasAll(eXpress.creator.LayoutPlacement.Single) &&
						canReplaceLeftPage
					)
						return true;
					return false;
				});
			}
			return false;
		});

		return availableSpreadDesigns;
	}, [allSpreadDesigns, dashboard, spread]);
}

export function useAreSpreadDesignsSupported() {
	// Only enable if the definition has spread designs AND pages contain a layoutHash
	// LayoutHash can be disabled by a DB Config
	return !!useAllSpreadDesigns()[0]?.pageDesigns[0]?.layoutHash;
}
